import { DOCUMENT } from '@angular/common';
import { ApplicationRef, ComponentRef, createComponent, EnvironmentInjector, inject, Injectable } from '@angular/core';
import { Observable, Subscriber, Subscription } from 'rxjs';
import { SzConfirmDialogComponent } from './sz-confirm-dialog.component';
import { SzConfirmDialogOptions } from './sz-confirm-dialog.types';

const CLOSE_ANIMATION_MS: number = 280;

@Injectable({ providedIn: 'root' })
export class SzConfirmDialogService {
  private readonly appRef: ApplicationRef = inject(ApplicationRef);
  private readonly envInjector: EnvironmentInjector = inject(EnvironmentInjector);
  private readonly document: Document = inject(DOCUMENT);

  confirm$(opts: SzConfirmDialogOptions): Observable<boolean> {
    return new Observable<boolean>((subscriber: Subscriber<boolean>): (() => void) => {
      const ref: ComponentRef<SzConfirmDialogComponent> = createComponent(SzConfirmDialogComponent, {
        environmentInjector: this.envInjector
      });

      this.applyInputs(ref, opts);
      this.appRef.attachView(ref.hostView);
      this.document.body.appendChild(ref.location.nativeElement);

      Promise.resolve().then((): void => ref.setInput('open', true));

      let destroyed: boolean = false;
      const teardown = (): void => {
        if (destroyed) return;
        destroyed = true;
        this.appRef.detachView(ref.hostView);
        ref.destroy();
      };
      const close = (result: boolean): void => {
        ref.setInput('open', false);
        setTimeout((): void => {
          subscriber.next(result);
          subscriber.complete();
          teardown();
        }, CLOSE_ANIMATION_MS);
      };

      const confirmSub: Subscription = ref.instance.confirm.subscribe((): void => close(true));
      const cancelSub: Subscription = ref.instance.cancel.subscribe((): void => close(false));

      return (): void => {
        confirmSub.unsubscribe();
        cancelSub.unsubscribe();
        teardown();
      };
    });
  }

  private applyInputs(ref: ComponentRef<SzConfirmDialogComponent>, opts: SzConfirmDialogOptions): void {
    ref.setInput('header', opts.header);
    if (opts.message !== undefined) ref.setInput('message', opts.message);
    if (opts.okLabel !== undefined) ref.setInput('okLabel', opts.okLabel);
    if (opts.cancelLabel !== undefined) ref.setInput('cancelLabel', opts.cancelLabel);
    if (opts.destructive !== undefined) ref.setInput('destructive', opts.destructive);
    if (opts.dismissOnScrim !== undefined) ref.setInput('dismissOnScrim', opts.dismissOnScrim);
    if (opts.dismissOnEsc !== undefined) ref.setInput('dismissOnEsc', opts.dismissOnEsc);
  }
}
